import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { Filter, Columns3 } from 'lucide-react';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { AccountingImportFiltersTab } from './accounting-import-filters-tab';
import { AccountingImportColumnsTab } from './accounting-import-columns-tab';
import { format } from 'date-fns';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import type { AccountingAsset } from '@/types/asset-import.types';
import { getCategoryNames } from '@/config/categories';

interface AccountingAssetsTableProps {
  assets: AccountingAsset[];
  onImport: (selectedAssets: AccountingAsset[]) => void;
  onCancel: () => void;
}

export function AccountingAssetsTable({ assets, onImport, onCancel }: AccountingAssetsTableProps) {
  const navigate = useNavigate();
  const {
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    setActiveTab,
    setIsExpanded,
  } = useRightPanel();

  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSelectionInitialized, setIsSelectionInitialized] = useState(false);
  const [assetEdits, setAssetEdits] = useState<Map<string, Partial<AccountingAsset>>>(new Map());
  const pageSize = 25;

  // Get available categories - include both Asset Manager categories and unique Financials categories
  const availableCategories = useMemo(() => {
    const assetManagerCategories = getCategoryNames();
    // Collect unique Financials categories that aren't already in Asset Manager categories
    const financialsCategories = assets
      .map(asset => asset.financialsCategory ?? asset.category)
      .filter((cat): cat is string => cat !== undefined && cat !== null && cat !== '')
      .filter(cat => !assetManagerCategories.includes(cat));
    const uniqueFinancialsCategories = Array.from(new Set(financialsCategories));
    // Combine and sort all categories
    return [...assetManagerCategories, ...uniqueFinancialsCategories].sort();
  }, [assets]);

  // Default column configuration
  const DEFAULT_COLUMNS = useMemo(() => [
    { id: 'assetCode', label: 'Asset Code', visible: true, order: 0 },
    { id: 'name', label: 'Asset Name', visible: true, order: 1 },
    { id: 'category', label: 'Category', visible: true, order: 2 },
    { id: 'manufacturer', label: 'Manufacturer', visible: false, order: 3 },
    { id: 'model', label: 'Model', visible: false, order: 4 },
    { id: 'serialNumber', label: 'Serial Number', visible: false, order: 5 },
    { id: 'purchaseDate', label: 'Purchase Date', visible: true, order: 6 },
    { id: 'purchasePrice', label: 'Purchase Price', visible: true, order: 7 },
    { id: 'supplier', label: 'Supplier', visible: false, order: 8 },
    { id: 'location', label: 'Location', visible: false, order: 9 },
    { id: 'invoiceReference', label: 'Invoice Reference', visible: false, order: 10 },
    { id: 'purchaseOrderNumber', label: 'PO Number', visible: false, order: 11 },
    { id: 'status', label: 'Status', visible: true, order: 12 },
  ], []);

  // Initialize columns on mount
  useEffect(() => {
    if (!isInitialized) {
      if (contextVisibleColumns.length === 0) {
        const defaultVisible = DEFAULT_COLUMNS.filter(col => col.visible).map(col => col.id);
        setVisibleColumns(defaultVisible);
      }
      if (contextColumnOrder.length === 0) {
        setColumnOrder(DEFAULT_COLUMNS.map(col => col.id));
      }
      setIsInitialized(true);
    }
  }, [isInitialized, contextVisibleColumns.length, contextColumnOrder.length, DEFAULT_COLUMNS, setVisibleColumns, setColumnOrder]);

  // Select all non-imported assets by default on mount
  useEffect(() => {
    if (!isSelectionInitialized && assets.length > 0) {
      const selectableIds = assets
        .filter(asset => asset.status !== 'already-imported')
        .map(asset => asset.id);
      setSelectedRowIds(new Set(selectableIds));
      setIsSelectionInitialized(true);
    }
  }, [isSelectionInitialized, assets, setSelectedRowIds]);

  // Get ordered visible columns
  const orderedVisibleColumns = useMemo(() => {
    const visibleCols = contextVisibleColumns.length > 0 ? contextVisibleColumns : DEFAULT_COLUMNS.filter(c => c.visible).map(c => c.id);
    const order = contextColumnOrder.length > 0 ? contextColumnOrder : DEFAULT_COLUMNS.map(c => c.id);
    
    return order
      .filter(id => visibleCols.includes(id))
      .map(id => DEFAULT_COLUMNS.find(col => col.id === id))
      .filter((col): col is typeof DEFAULT_COLUMNS[0] => col !== undefined);
  }, [contextVisibleColumns, contextColumnOrder, DEFAULT_COLUMNS]);

  // Apply filters
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];

    // Filter by "show excluded" (already imported)
    if (!tableFilters.showExcluded) {
      filtered = filtered.filter(asset => asset.status !== 'already-imported');
    }

    // Filter by categories
    if (tableFilters.selectedCategories && tableFilters.selectedCategories.length > 0) {
      filtered = filtered.filter(asset => 
        tableFilters.selectedCategories.includes(asset.category)
      );
    }

    return filtered;
  }, [assets, tableFilters]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const newAssets = assets.filter(a => a.status !== 'already-imported');
    const selected = Array.from(selectedRowIds).map(id => assets.find(a => a.id === id)).filter(Boolean) as AccountingAsset[];
    const alreadyImported = assets.filter(a => a.status === 'already-imported');
    const excluded = assets.length - newAssets.length;

    return {
      newAssets: newAssets.length,
      selected: selected.length,
      alreadyImported: alreadyImported.length,
      excluded,
    };
  }, [assets, selectedRowIds]);

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAssets = filteredAssets.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tableFilters]);

  // Selection handlers
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const selectableIds = paginatedAssets
        .filter(asset => asset.status !== 'already-imported')
        .map(asset => asset.id);
      setSelectedRowIds(new Set(selectableIds));
    } else {
      setSelectedRowIds(new Set());
    }
  }, [paginatedAssets, setSelectedRowIds]);

  const handleSelectionChange = useCallback((assetId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(assetId);
      } else {
        newSet.delete(assetId);
      }
      return newSet;
    });
  }, [setSelectedRowIds]);

  const allSelected = paginatedAssets
    .filter(asset => asset.status !== 'already-imported')
    .every(asset => selectedRowIds.has(asset.id)) && paginatedAssets.some(asset => asset.status !== 'already-imported');

  // Edit handlers
  const handleEditField = useCallback((assetId: string, field: keyof AccountingAsset, value: string) => {
    setAssetEdits(prev => {
      const newEdits = new Map(prev);
      const currentEdit = newEdits.get(assetId) || {};
      newEdits.set(assetId, { ...currentEdit, [field]: value });
      return newEdits;
    });
  }, []);

  const getDisplayValue = useCallback((asset: AccountingAsset, field: keyof AccountingAsset) => {
    const edits = assetEdits.get(asset.id);
    if (edits && field in edits) {
      return edits[field] as string;
    }
    return asset[field] as string;
  }, [assetEdits]);

  // Import handler - merge edits with assets
  const handleImport = () => {
    const selectedAssets = assets
      .filter(asset => selectedRowIds.has(asset.id))
      .map(asset => {
        const edits = assetEdits.get(asset.id);
        if (edits) {
          return { ...asset, ...edits };
        }
        return asset;
      });
    onImport(selectedAssets);
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return value.toLocaleString('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Get unique categories for filters
  const categories = useMemo(() => {
    return Array.from(new Set(assets.map(a => a.category))).sort();
  }, [assets]);

  // Right panel tabs
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: (
        <AccountingImportFiltersTab
          categories={categories}
          showExcluded={tableFilters.showExcluded ?? false}
          selectedCategories={tableFilters.selectedCategories ?? []}
          onShowExcludedChange={(show) => setTableFilters({ ...tableFilters, showExcluded: show })}
          onCategoriesChange={(cats) => setTableFilters({ ...tableFilters, selectedCategories: cats })}
        />
      ),
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns3,
      content: (
        <AccountingImportColumnsTab
          columns={DEFAULT_COLUMNS}
          visibleColumns={contextVisibleColumns.length > 0 ? contextVisibleColumns : DEFAULT_COLUMNS.filter(c => c.visible).map(c => c.id)}
          columnOrder={contextColumnOrder.length > 0 ? contextColumnOrder : DEFAULT_COLUMNS.map(c => c.id)}
          onVisibleColumnsChange={setVisibleColumns}
          onColumnOrderChange={setColumnOrder}
        />
      ),
      badgeCount: orderedVisibleColumns.length,
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* KPI Cards Section - Separate from table */}
        <div className="flex-shrink-0 p-6 bg-background">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* New Assets */}
            <StaticMetricCardAlt
              title="New Assets"
              value={summary.newAssets.toString()}
              change="Available to import"
              changeLabel=""
              icon={Package}
              variant="info"
            />

            {/* Selected */}
            <StaticMetricCardAlt
              title="Selected"
              value={summary.selected.toString()}
              change="Selected for import"
              changeLabel=""
              icon={CheckCircle2}
              variant="info"
            />

            {/* Already Imported */}
            <StaticMetricCardAlt
              title="Already Imported"
              value={summary.alreadyImported.toString()}
              change="Previously imported"
              changeLabel=""
              icon={CheckCircle2}
              variant="info"
            />

            {/* Excluded */}
            <StaticMetricCardAlt
              title="Excluded"
              value={summary.excluded.toString()}
              change="Already in system"
              changeLabel=""
              icon={AlertCircle}
              variant="info"
            />
          </div>
        </div>

        {/* Table Section Header */}
        <div className="px-6 py-4 bg-white dark:bg-black border-t border-b border-slate-200 dark:border-slate-800">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Review Assets
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Select the assets you want to import into your asset register. Assets already imported are disabled.
          </p>
        </div>

        {/* Table Container - Full width, no padding */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 bg-white dark:bg-black">
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                  {/* Checkbox column */}
                  <TableHead className="h-12 font-semibold relative w-20">
                    <div className="flex items-center justify-center px-3">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>

                  {/* Dynamic columns */}
                  {orderedVisibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={`h-12 font-semibold relative ${
                        column.id === 'purchasePrice' ? 'text-right' : ''
                      }`}
                    >
                      {column.label}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {paginatedAssets.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={orderedVisibleColumns.length + 1}
                      className="h-96"
                    >
                      <div className="flex flex-col items-center justify-center space-y-4 py-12">
                        <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                          <Package className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                        </div>
                        <div className="space-y-2 text-center">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            No assets found
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            {filteredAssets.length !== assets.length
                              ? "Try adjusting your filters to find what you're looking for"
                              : "No assets available to import"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAssets.map((asset, index) => (
                    <TableRow
                      key={asset.id}
                      className={`
                        ${index % 2 === 0
                          ? 'bg-white dark:bg-slate-950'
                          : 'bg-slate-50/50 dark:bg-slate-900/50'
                        }
                        hover:bg-slate-100 dark:hover:bg-slate-800
                        transition-colors
                        border-b border-slate-100 dark:border-slate-800
                        ${asset.status === 'already-imported' ? 'opacity-50' : ''}
                      `}
                    >
                      {/* Checkbox cell */}
                      <TableCell className="w-20">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedRowIds.has(asset.id)}
                            onCheckedChange={(checked) =>
                              handleSelectionChange(asset.id, checked as boolean)
                            }
                            disabled={asset.status === 'already-imported'}
                          />
                        </div>
                      </TableCell>

                      {/* Dynamic cells */}
                      {orderedVisibleColumns.map((column) => {
                        if (column.id === 'assetCode') {
                          const isDisabled = asset.status === 'already-imported';
                          return (
                            <TableCell key={column.id} className="px-2 py-2">
                              <Input
                                value={getDisplayValue(asset, 'assetCode')}
                                onChange={(e) => handleEditField(asset.id, 'assetCode', e.target.value)}
                                disabled={isDisabled}
                                className="h-8 text-sm font-medium"
                              />
                            </TableCell>
                          );
                        }
                        if (column.id === 'name') {
                          const isDisabled = asset.status === 'already-imported';
                          return (
                            <TableCell key={column.id} className="px-2 py-2">
                              <Input
                                value={getDisplayValue(asset, 'description')}
                                onChange={(e) => handleEditField(asset.id, 'description', e.target.value)}
                                disabled={isDisabled}
                                className="h-8 text-sm"
                              />
                            </TableCell>
                          );
                        }
                        if (column.id === 'category') {
                          const isDisabled = asset.status === 'already-imported';
                          return (
                            <TableCell key={column.id} className="px-2 py-2">
                              <Select
                                value={getDisplayValue(asset, 'category')}
                                onValueChange={(value: string) => handleEditField(asset.id, 'category', value)}
                                disabled={isDisabled}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableCategories.map((cat) => (
                                    <SelectItem key={cat} value={cat}>
                                      {cat}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          );
                        }
                        if (column.id === 'manufacturer') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {asset.manufacturer || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'model') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {asset.model || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'serialNumber') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300 font-mono text-xs">
                              {asset.serialNumber || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'purchaseDate') {
                          const date = asset.purchaseDate ? new Date(asset.purchaseDate) : null;
                          const isValidDate = date && !isNaN(date.getTime());
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">
                              {isValidDate ? format(date, 'dd/MM/yyyy') : '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'purchasePrice') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
                              {formatCurrency(asset.purchasePrice)}
                            </TableCell>
                          );
                        }
                        if (column.id === 'supplier') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {asset.supplier || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'location') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {asset.location || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'invoiceReference') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300 font-mono text-xs">
                              {asset.invoiceReference || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'purchaseOrderNumber') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3 text-slate-700 dark:text-slate-300 font-mono text-xs">
                              {asset.purchaseOrderNumber || '-'}
                            </TableCell>
                          );
                        }
                        if (column.id === 'status') {
                          return (
                            <TableCell key={column.id} className="px-4 py-3">
                              {asset.status === 'already-imported' ? (
                                <Badge className="bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400">
                                  Already Imported
                                </Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                                  New
                                </Badge>
                              )}
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="border-t bg-card px-6 py-4 flex-shrink-0">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Left: Entry count */}
            <div className="text-sm text-muted-foreground flex-shrink-0">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredAssets.length)} of {filteredAssets.length} assets
              {filteredAssets.length !== assets.length && ` (filtered from ${assets.length} total)`}
            </div>

            {/* Center: Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}

            {/* Right: Action buttons */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={selectedRowIds.size === 0}
              >
                Import {selectedRowIds.size > 0 ? `(${selectedRowIds.size})` : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} defaultTab="filters" />
    </PageWithRightPanel>
  );
}

export default AccountingAssetsTable;
